import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
  Field,
  FieldLabel,
  Icon,
  IconPicker,
  Input,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  ScrollArea,
  Separator,
  Spinner,
  Switch,
  Text,
  type Color,
  type IconName,
} from "@salestrak/ui";
import {
  useIssueType,
  useIssueTypeMutations,
  type IssueTypeFieldItem,
} from "#app/hooks/settings/use-issue-type-mutations";
import {
  SettingsGroup,
  SettingsGroupHeader,
  SettingsGroupRow,
} from "./settings-group";
import { calculateSortOrder } from "@salestrak/ui";
import { PageHeader, PageHeaderBack, PageHeaderTitle } from "#app/components/page-header";
import { IssueTypeDeleteDialog } from "./issue-type-delete-dialog";
import { CustomFieldSelector } from "#app/components/entity-selectors";
import { CUSTOM_FIELD_POOL_ID } from "#app/components/entity-selectors/hooks";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import { toastMutationError } from "#app/lib/utils/toast-errors";

/**
 * Tinted rounded tile around a colored glyph, for the two field-row leading
 * slots. `Icon` has no padded box of its own, so the tile is a Box naming the
 * color once and letting the wash and the glyph both read it.
 */
function FieldTypeGlyph({ color, icon }: { color: Color; icon: IconName }) {
  return (
    <Box
      align="center"
      className="bg-c-soft"
      data-color={color}
      justify="center"
      padding="xs"
      rounded="md"
    >
      <Icon className="text-c" name={icon} size="md" />
    </Box>
  );
}

interface IssueTypeEditViewProps {
  issueTypeId: string;
  onBack: () => void;
}

export function IssueTypeEditView({
  issueTypeId,
  onBack,
}: IssueTypeEditViewProps) {
  const { issueType, isLoading, error } = useIssueType(issueTypeId);
  const {
    updateIssueType,
    deleteIssueType,
    setDefaultIssueType,
    reorderIssueTypeField,
    optimisticReorderField,
  } = useIssueTypeMutations();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState<string>("settings");
  const [color, setColor] = useState<string>("grey");
  const [showDelete, setShowDelete] = useState(false);
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const [pendingFieldIds, setPendingFieldIds] = useState<string[]>([]);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (issueType) {
      setName(issueType.name);
      setDescription(issueType.description ?? "");
      setIcon(issueType.icon?.icon ?? "settings");
      setColor(issueType.icon?.color ?? "grey");
    }
  }, [issueType]);

  const debouncedUpdate = useCallback(
    (input: Record<string, any>) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        updateIssueType(issueTypeId, input);
      }, 500);
    },
    [issueTypeId, updateIssueType],
  );

  function handleNameChange(value: string) {
    setName(value);
    debouncedUpdate({ name: value });
  }

  function handleDescriptionChange(value: string) {
    setDescription(value);
    debouncedUpdate({ description: value });
  }

  async function handleSetDefault() {
    await setDefaultIssueType(issueTypeId);
  }

  async function handleDelete() {
    if (!issueType) return;
    await deleteIssueType(issueType.id, issueType.issueTypeId);
    onBack();
  }

  async function handleAddFields(fieldIds: string[]) {
    if (!issueType) return;
    const existingFields = (issueType.fields ?? []).map((f) => ({
      customFieldId: f.customFieldId,
      required: f.required,
    }));
    const existingIds = new Set(existingFields.map((f) => f.customFieldId));
    const newFields = fieldIds
      .filter((id) => !existingIds.has(id))
      .map((id) => ({
        customFieldId: id,
        required: false,
      }));
    if (newFields.length === 0) return;
    await updateIssueType(issueTypeId, {
      fields: [...existingFields, ...newFields],
    });
  }

  const handleFieldRequiredChange = useCallback(
    async (customFieldId: string, required: boolean) => {
      if (!issueType) return;
      const fields = (issueType.fields ?? []).map((f) => ({
        customFieldId: f.customFieldId,
        required: f.customFieldId === customFieldId ? required : f.required,
      }));
      await updateIssueType(issueTypeId, { fields });
    },
    [issueType, issueTypeId, updateIssueType],
  );

  const handleRemoveField = useCallback(
    async (customFieldId: string) => {
      if (!issueType) return;
      const fields = (issueType.fields ?? [])
        .filter((f) => f.customFieldId !== customFieldId)
        .map((f) => ({
          customFieldId: f.customFieldId,
          required: f.required,
        }));
      await updateIssueType(issueTypeId, { fields });
    },
    [issueType, issueTypeId, updateIssueType],
  );

  const sortedFields = useMemo<IssueTypeFieldItem[]>(() => {
    const fields = (issueType?.fields ?? []) as IssueTypeFieldItem[];
    return [...fields].sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
    );
  }, [issueType?.fields]);

  const fieldIds = useMemo(
    () => sortedFields.map((f) => f.customFieldId),
    [sortedFields],
  );

  const handleReorder = useCallback(
    async (activeId: string, overId: string) => {
      if (!issueType) return;
      const dragged = sortedFields.find((f) => f.customFieldId === activeId);
      const target = sortedFields.find((f) => f.customFieldId === overId);
      if (!dragged || !target) return;

      const others = sortedFields.filter(
        (f) => f.customFieldId !== dragged.customFieldId,
      );
      const overIndex = others.findIndex(
        (f) => f.customFieldId === target.customFieldId,
      );
      if (overIndex < 0) return;

      const placeAfter = dragged.sortOrder < target.sortOrder;
      const insertIndex = placeAfter ? overIndex + 1 : overIndex;
      const upper = others[insertIndex - 1]?.sortOrder ?? null;
      const lower = others[insertIndex]?.sortOrder ?? null;
      const newSortOrder = calculateSortOrder(upper, lower);

      if (dragged.sortOrder === newSortOrder) return;

      optimisticReorderField(issueType.id, dragged.customFieldId, newSortOrder);

      try {
        await reorderIssueTypeField(
          issueType.id,
          dragged.customFieldId,
          newSortOrder,
        );
      } catch (err) {
        toastMutationError(err, "Failed to reorder field");
      }
    },
    [issueType, sortedFields, optimisticReorderField, reorderIssueTypeField],
  );

  const renderFieldOverlay = useCallback(
    (id: string) => {
      const field = sortedFields.find((f) => f.customFieldId === id);
      if (!field?.field) return null;
      const meta = getFieldTypeMeta(field.field.type);
      return (
        <SettingsGroupRow
          label={field.field.name}
          subtitle={field.field.description ?? undefined}
          leading={<FieldTypeGlyph color={meta.color} icon={meta.icon} />}
        />
      );
    },
    [sortedFields],
  );

  const excludedFieldIds = useMemo(
    () => sortedFields.map((f) => f.customFieldId),
    [sortedFields],
  );

  if (isLoading && !issueType) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Spinner />
      </Box>
    );
  }

  if (error && !issueType) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Unable to load project type</EmptyTitle>
            <EmptyDescription>{error.message}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </Box>
    );
  }

  if (!issueType) return null;

  const fieldCount = sortedFields.length;

  function handleIconChange(newIcon: IconName) {
    setIcon(newIcon);
    updateIssueType(issueTypeId, { icon: { icon: newIcon, color } });
  }

  function handleColorChange(newColor: Color) {
    setColor(newColor);
    updateIssueType(issueTypeId, { icon: { icon, color: newColor } });
  }

  return (
    <Box direction="column" fill="flex">
      <ScrollArea>
        <PageHeader centered>
          <PageHeaderBack label="Project Types" onClick={onBack} />
          <PageHeaderTitle
            adornment={
              issueType.isDefault ? <Badge>Default</Badge> : undefined
            }
          >
            {issueType.name}
          </PageHeaderTitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box gap="xl" fill="width" maxWidth={800}>
            <Box gap="md">
              {/* Name & Description Card */}
              <Card render={<Box padding="md" />}>
                <Box direction="row" gap="lg" wrap>
                  {/* Box's `flex` token union is 1/none/auto/initial — arbitrary
                      grow factors are not tokens, so the 2:3 name/description
                      split rides an authored flex class. */}
                  <Box flex={2} minWidth={200}>
                    <Field>
                      <FieldLabel>Name & Icon</FieldLabel>
                      <Box direction="row" gap="sm" align="center" fill="width">
                        <IconPicker
                          icon={icon as IconName}
                          color={color as Color}
                          onIconChange={handleIconChange}
                          onColorChange={handleColorChange}
                          size="sm"
                        />
                        <Box flex={1}>
                          <Input
                            value={name}
                            onChange={(e) => handleNameChange(e.target.value)}
                            placeholder="Type name"
                          />
                        </Box>
                      </Box>
                    </Field>
                  </Box>
                  <Box flex={3} minWidth={200}>
                    <Field>
                      <FieldLabel>Description</FieldLabel>
                      <Input
                        value={description}
                        onChange={(e) => handleDescriptionChange(e.target.value)}
                        placeholder="Optional description"
                      />
                    </Field>
                  </Box>
                </Box>
              </Card>

              {/* Set as default */}
              <SettingsGroup>
                <SettingsGroupRow
                  label="Set type as default"
                  subtitle="Default to this type when creating a new project."
                >
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSetDefault}
                    disabled={issueType.isDefault}
                  >
                    Set as default
                  </Button>
                </SettingsGroupRow>
              </SettingsGroup>
            </Box>

            {/* Custom Fields Section */}
            <SettingsGroup
              label="Custom fields"
              subtitle="Select and require custom fields to be assigned to this type on creation. Drag to reorder."
              sortable={{
                items: fieldIds,
                onReorder: handleReorder,
                renderOverlay: renderFieldOverlay,
              }}
            >
              <SettingsGroupHeader>
                <Text size="sm" weight="medium">
                  {fieldCount} Custom {fieldCount === 1 ? "field" : "fields"}
                </Text>
                <CustomFieldSelector
                  value={pendingFieldIds}
                  onValueChange={setPendingFieldIds}
                  exclude={excludedFieldIds}
                  onOpenChange={(next) => {
                    setAddFieldOpen(next);
                    if (!next && pendingFieldIds.length > 0) {
                      handleAddFields(pendingFieldIds);
                      setPendingFieldIds([]);
                    }
                  }}
                >
                  <EntitySelectorTrigger
                    render={
                      <Button
                        size="icon-sm"
                        variant="ghost"
                        aria-label="Add custom field"
                      >
                        <Icon name="plus" size="md" />
                      </Button>
                    }
                  />
                  <EntitySelectorContent size="sm" align="end">
                    <EntitySelectorInput />
                    <EntitySelectorPool value={CUSTOM_FIELD_POOL_ID} />
                  </EntitySelectorContent>
                </CustomFieldSelector>
              </SettingsGroupHeader>

              {sortedFields.map((field) => {
                const fieldData = field.field;
                if (!fieldData) return null;
                const meta = getFieldTypeMeta(fieldData.type);
                return (
                  <SettingsGroupRow
                    key={field.customFieldId}
                    sortableId={field.customFieldId}
                    label={
                      <>
                        {fieldData.name}
                        {field.required ? (
                          <Text size="sm" variant="muted">
                            {" · Required on creation"}
                          </Text>
                        ) : null}
                      </>
                    }
                    subtitle={fieldData.description ?? undefined}
                    leading={
                      <FieldTypeGlyph color={meta.color} icon={meta.icon} />
                    }
                  >
                    <Menu>
                      <MenuTrigger
                        render={
                          <Button
                            variant="ghost"
                            size="icon-xs"
                            aria-label="Field options"
                          >
                            <Icon name="more-vertical" size="md" />
                          </Button>
                        }
                      />
                      <MenuPopup align="end" className="min-w-[220px]">
                        <Box
                          direction="row"
                          align="center"
                          justify="between"
                          paddingX="sm"
                          paddingY="xs"
                        >
                          <Text size="sm">Required on creation</Text>
                          <Switch
                            checked={field.required}
                            onCheckedChange={(required) =>
                              handleFieldRequiredChange(
                                field.customFieldId,
                                required,
                              )
                            }
                          />
                        </Box>
                        <Separator />
                        <MenuItem
                          onClick={() =>
                            handleRemoveField(field.customFieldId)
                          }
                        >
                          <Icon
                            name="trash-2"
                            size="md"
                            color="destructive"
                          />
                          <Text variant="destructive">Remove field</Text>
                        </MenuItem>
                      </MenuPopup>
                    </Menu>
                  </SettingsGroupRow>
                );
              })}
            </SettingsGroup>

            {/* Danger Zone */}
            <SettingsGroup label="Danger zone">
              <SettingsGroupRow label="Remove type from your workspace">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDelete(true)}
                  disabled={issueType.isDefault}
                >
                  <Text variant="destructive">Delete type...</Text>
                </Button>
              </SettingsGroupRow>
            </SettingsGroup>
          </Box>
        </Box>
      </ScrollArea>

      <IssueTypeDeleteDialog
        open={showDelete}
        onOpenChange={setShowDelete}
        issueTypeName={issueType.name}
        onConfirm={handleDelete}
      />
    </Box>
  );
}
